import React, { useLayoutEffect, useRef } from 'react';
import { Temporal } from 'temporal-polyfill';
import { ITimeZone } from '../../types/ITimeZone';

interface IProps {
	/**
	 * First day of the header
	 */
	from: Temporal.PlainDate;
	/**
	 * Last day visible in the header
	 */
	to: Temporal.PlainDate;
	/**
	 * The day where the user lives.
	 * Do not confuse with currentDay.
	 */
	today: Temporal.PlainDate;
	/**
	 * The active day in the calendar.
	 * Do not confuse with today.
	 */
	currentDay: Temporal.PlainDate;
	/**
	 * TimeZone used in the header
	 */
	timeZone: ITimeZone;
	/**
	 * Function handler when the user clicks on a day in the header.
	 */
	onDayClick?: (day: Temporal.PlainDate, index: number) => void;
	/**
	 * Function handler when the user swipes to between weeks.
	 */
	onWeekChange?: (firstDay: Temporal.PlainDate) => void;
	/**
	 * Function handler executed when the user clicks on the "today" button.
	 */
	onTodayClick: () => void;
}

export function useWeekCalendarHeaderController(props: IProps) {
	const days = React.useMemo(() => _generateDays(props.from, props.to), [props.from, props.to]);
	const ref = useRef<HTMLDivElement>(null);
	const prevWeekDays = days.map((d) => d.subtract({ days: days.length }));
	const nextWeekDays = days.map((d) => d.add({ days: days.length }));

	useLayoutEffect(() => {
		if (ref.current) {
			const r = ref.current;
			const weekWidth = ref.current.clientWidth;
			const scrollTo = weekWidth / 2;
			ref.current.scrollTo({
				left: scrollTo + 1,
				behavior: 'instant',
			});

			const handleScroll = () => {
				if (r) {
					const { scrollLeft, scrollWidth, clientWidth } = r;

					if (scrollLeft === 0) {
						if (props.onWeekChange) {
							props.onWeekChange(prevWeekDays[0]);
						}
					}

					if (scrollLeft + clientWidth >= scrollWidth) {
						if (props.onWeekChange) {
							props.onWeekChange(nextWeekDays[0]);
						}
					}
				}
			};

			r.addEventListener('scroll', handleScroll);

			return () => {
				r.removeEventListener('scroll', handleScroll);
			};
		}
	}, [nextWeekDays, prevWeekDays, props, days]);

	return {
		ref,
		days,
		nextWeekDays,
		prevWeekDays,
	};
}

function _generateDays(from: Temporal.PlainDate, to: Temporal.PlainDate): Array<Temporal.PlainDate> {
	let current = from;
	const days = [];

	while (Temporal.PlainDate.compare(current, to) < 0) {
		days.push(current);
		current = current.add({ days: 1 });
	}

	return days;
}
